import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { deleteSupporterVideo } from '#src/app/shared/api/supporterVoices';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import type { PetitionVideoSurface, SupporterVideo } from '../../../../../../types';

type UseDeleteVideoLogicProps = {
	video: SupporterVideo;
	petitionId: string;
	renderedOn: PetitionVideoSurface;
	onResult: (result: { value: 'success' | 'error' | undefined }) => void;
	onOpenChange: (open: boolean) => void;
};

type UseDeleteVideoLogicReturn = {
	isDeleting: boolean;
	deleteMessage: 'success' | 'error' | undefined;
	handleCancel: () => void;
	handleDelete: () => void;
};

function createTrackingData(
	petitionId: string,
	video: SupporterVideo,
	renderedOn: PetitionVideoSurface,
): TrackingProperties {
	const base: TrackingProperties = {
		petition_id: petitionId,
		page: renderedOn,
	};
	return video.videoUrl
		? { ...base, video_id: video.id, video_creator_id: video.user?.id }
		: { ...base, comment_id: video.id, comment_creator_id: video.user?.id };
}

export function useDeleteVideoLogic({
	video,
	petitionId,
	renderedOn,
	onResult,
	onOpenChange,
}: UseDeleteVideoLogicProps): UseDeleteVideoLogicReturn {
	const [deleteMessage, setDeleteMessage] = useState<'success' | 'error' | undefined>(undefined);
	const utilityContext = useUtilityContext();
	const { track, trackError } = useCampaignTracking();

	const trackingData = useMemo(
		() => createTrackingData(petitionId, video, renderedOn),
		[petitionId, video, renderedOn],
	);

	const { mutate: deleteVideo, isPending: isDeleting } = useMutation({
		mutationFn: async () => deleteSupporterVideo({ videoId: video.id, utilityContext }),
		onSuccess: (deleteResult) => {
			// A resolved request that's a logical failure (not `DeletePetitionSupporterVideoSuccess`) isn't a thrown error.
			const response = deleteResult?.deletePetitionSupporterVideo;
			if (response?.__typename === 'DeletePetitionSupporterVideoSuccess') {
				track('supporter_voice_delete_success', trackingData);
				setDeleteMessage('success');
				return;
			}
			track('supporter_voice_delete_error', {
				...trackingData,
				...(response?.__typename === 'DeletePetitionSupporterVideoFailure' ? { errors: response.reason } : {}),
			});
			setDeleteMessage('error');
		},
		onError: (err) => {
			trackError('supporter_voice_delete_error', err, { params: trackingData });
			setDeleteMessage('error');
		},
	});

	useEffect(() => {
		if (deleteMessage === 'success') {
			setTimeout(() => {
				onResult({ value: deleteMessage });
				onOpenChange(false);
			}, 1000);
		}
	}, [deleteMessage, onOpenChange, onResult]);

	const handleCancel = useCallback(() => {
		track('supporter_voices_delete_modal_cancel_button_click', trackingData);
		onResult({ value: deleteMessage });
		onOpenChange(false);
	}, [deleteMessage, track, trackingData, onOpenChange, onResult]);

	const handleDelete = useCallback(() => {
		track('supporter_voices_delete_modal_delete_button_click', trackingData);
		deleteVideo();
	}, [deleteVideo, track, trackingData]);

	return { isDeleting, deleteMessage, handleCancel, handleDelete };
}
